import axios from 'axios';
import { createAuthRefreshInterceptor } from '@/lib/authSession';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Techo HTTP sync de plataforma (ms). Debe alinearse con backend HTTP_TURN_BUDGET_SEC (default 600). */
export const DEFAULT_HTTP_TURN_BUDGET_SEC = 600;
export const HTTP_TURN_CLIENT_MARGIN_MS = 5000;

export function httpTurnClientTimeoutMs(budgetSec = DEFAULT_HTTP_TURN_BUDGET_SEC) {
  const sec = Number(budgetSec) || DEFAULT_HTTP_TURN_BUDGET_SEC;
  return sec * 1000 + HTTP_TURN_CLIENT_MARGIN_MS;
}

const api = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

/** Mensaje legible desde respuestas FastAPI (detail string | list | object). */
export function formatApiError(err) {
  const d = err?.response?.data?.detail;
  if (d == null) return err?.message || 'Error de red o sin respuesta del servidor';
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) {
    return d
      .map((x) =>
        typeof x === 'object' && x != null && x.msg
          ? `${Array.isArray(x.loc) ? x.loc.join('.') : ''}: ${x.msg}`.replace(/^:\s*/, '')
          : JSON.stringify(x)
      )
      .join('; ');
  }
  if (typeof d === 'object') {
    if (typeof d.detail === 'string') return d.detail;
    if (typeof d.message === 'string') return d.message;
    return JSON.stringify(d);
  }
  return String(d);
}

function tenantCapsuleFromApiUrl(url) {
  const u = String(url || '');
  const mAgent = u.match(/\/skills\/agent\/[^/]+\/([^/]+)\//);
  if (mAgent) return { capsule_id: mAgent[1] };
  const mTen = u.match(/\/tenants\/[^/]+\/capsules\/([^/]+)\//);
  if (mTen) return { capsule_id: mTen[1] };
  return {};
}

/**
 * Identidad del build desplegado. No requiere sesion: se usa para mostrar la version
 * en la UI y responder "que version tiene instalado este cliente" sin pedir SSH.
 * `_skipAuthRefresh` evita que un 401 de otra llamada dispare refresh por esta.
 */
export const healthAPI = {
  get: () => api.get('/health', { _skipAuthRefresh: true }),
};

export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  // El segundo paso del login con contraseña (2026-10-13): con el permiso temporal que devuelve
  // `/auth/login`. Ninguno de los tres refresca la sesión: todavía no hay sesión.
  configurarSegundoFactor: (desafio) => api.post('/auth/segundo-factor/configuracion', { desafio }),
  activarSegundoFactor: (desafio, codigo) => api.post('/auth/segundo-factor/activar', { desafio, codigo }),
  verificarSegundoFactor: (desafio, codigo) => api.post('/auth/segundo-factor/verificar', { desafio, codigo }),
  register: (data) => api.post('/auth/register', data),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
  refresh: () => api.post('/auth/refresh', null, { _skipAuthRefresh: true }),
  wsToken: () => api.get('/auth/ws-token'),
  oidcMicrosoftStatus: (tenantId) =>
    api.get('/auth/oidc/microsoft/status', { params: tenantId ? { tenant_id: tenantId } : {} }),
  // Solo admin. `configuracion` devuelve el secreto enmascarado con `***`, y guardar ese
  // valor tal cual **no** lo pisa: es como la pantalla evita romper el ingreso al guardar
  // sin haber reescrito el secreto.
  oidcConfiguracion: () => api.get('/auth/oidc/microsoft/configuracion'),
  guardarOidcConfiguracion: (data) => api.put('/auth/oidc/microsoft/configuracion', data),
  oidcMicrosoftStartUrl: (tenantId) => {
    const q = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : '';
    return `${API}/auth/oidc/microsoft/start${q}`;
  },
};

createAuthRefreshInterceptor(api, () => authAPI.refresh());

export const tenantsAPI = {
  list: () => api.get('/tenants'),
  get: (id) => api.get(`/tenants/${id}`),
  update: (id, data) => api.put(`/tenants/${id}`, data),
  listLLMProviders: (id) => api.get(`/tenants/${id}/llm-providers`),
  createLLMProvider: (id, data) => api.post(`/tenants/${id}/llm-providers`, data),
  updateLLMProvider: (id, providerRef, data) => api.put(`/tenants/${id}/llm-providers/${providerRef}`, data),
  deleteLLMProvider: (id, providerRef) => api.delete(`/tenants/${id}/llm-providers/${providerRef}`),
  testLLMProvider: (id, providerRef) => api.post(`/tenants/${id}/llm-providers/${providerRef}/test`),
  getIdentity: (id) => api.get(`/tenants/${id}/identity`),
  putIdentity: (id, identity) => api.put(`/tenants/${id}/identity`, { identity }),
};

export const capsulesAPI = {
  list: (tenantId) => api.get(`/tenants/${tenantId}/capsules`),
  get: (tenantId, capsuleId) => api.get(`/tenants/${tenantId}/capsules/${capsuleId}`),
  create: (tenantId, data) => api.post(`/tenants/${tenantId}/capsules`, data),
  update: (tenantId, capsuleId, data) => api.put(`/tenants/${tenantId}/capsules/${capsuleId}`, data),
  purgeHard: (tenantId, capsuleId, body) =>
    api.post(`/tenants/${tenantId}/capsules/${capsuleId}/purge`, body),
};

export const agentsAPI = {
  list: (tenantId, capsuleId) => api.get(`/tenants/${tenantId}/capsules/${capsuleId}/agents`),
  get: (tenantId, capsuleId, agentId) => api.get(`/tenants/${tenantId}/capsules/${capsuleId}/agents/${agentId}`),
  create: (tenantId, capsuleId, data) => api.post(`/tenants/${tenantId}/capsules/${capsuleId}/agents`, data),
  update: (tenantId, capsuleId, agentId, data) => api.put(`/tenants/${tenantId}/capsules/${capsuleId}/agents/${agentId}`, data),
  quePuedeHacer: (tenantId, capsuleId, agentId) => api.get(`/tenants/${tenantId}/capsules/${capsuleId}/agents/${agentId}/que-puede-hacer`),
  getConfigs: (tenantId, capsuleId, agentId) => api.get(`/tenants/${tenantId}/capsules/${capsuleId}/agents/${agentId}/configs/active`),
  createConfig: (tenantId, capsuleId, agentId, data) => api.post(`/tenants/${tenantId}/capsules/${capsuleId}/agents/${agentId}/configs`, data),
  delete: (tenantId, capsuleId, agentId) => api.delete(`/tenants/${tenantId}/capsules/${capsuleId}/agents/${agentId}`),
};

export const conversationsAPI = {
  // La misma lectura que la ficha del caso, del lado de la conversacion.
  actividadAgentica: (tenantId, capsuleId, conversationId) =>
    api.get(`/tenants/${tenantId}/capsules/${capsuleId}/conversations/${conversationId}/actividad-agentica`),
  list: (tenantId, capsuleId, status) => {
    const params = status ? `?status=${status}` : '';
    return api.get(`/tenants/${tenantId}/capsules/${capsuleId}/conversations${params}`);
  },
  get: (tenantId, capsuleId, convId) => api.get(`/tenants/${tenantId}/capsules/${capsuleId}/conversations/${convId}`),
  patchRoutingMetadata: (tenantId, capsuleId, convId, data) =>
    api.patch(`/tenants/${tenantId}/capsules/${capsuleId}/conversations/${convId}/routing-metadata`, data),
  delete: (tenantId, capsuleId, convId) => api.delete(`/tenants/${tenantId}/capsules/${capsuleId}/conversations/${convId}`),
  purgeAll: (tenantId, body) => api.post(`/tenants/${tenantId}/conversations/purge-all`, body),
  takeover: (tenantId, capsuleId, convId, reason) => api.post(`/tenants/${tenantId}/capsules/${capsuleId}/conversations/${convId}/hitl/takeover`, { reason }),
  handback: (tenantId, capsuleId, convId) => api.post(`/tenants/${tenantId}/capsules/${capsuleId}/conversations/${convId}/hitl/handback`),
  sendMessage: (tenantId, capsuleId, convId, data) => api.post(`/tenants/${tenantId}/capsules/${capsuleId}/conversations/${convId}/messages`, data),
  approveOutbound: (tenantId, capsuleId, convId) =>
    api.post(`/tenants/${tenantId}/capsules/${capsuleId}/conversations/${convId}/outbound/approve`),
  rejectOutbound: (tenantId, capsuleId, convId, data) =>
    api.post(`/tenants/${tenantId}/capsules/${capsuleId}/conversations/${convId}/outbound/reject`, data || {}),
  merge: (tenantId, capsuleId, targetConvId, data) =>
    api.post(`/tenants/${tenantId}/capsules/${capsuleId}/conversations/${targetConvId}/merge`, data),
  mergeSuggestions: (tenantId, capsuleId, convId) =>
    api.get(`/tenants/${tenantId}/capsules/${capsuleId}/conversations/${convId}/merge-suggestions`),
};

export const inboxAPI = {
  list: (tenantId, params = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v === undefined || v === null || v === '') return;
      if (Array.isArray(v)) v.forEach((item) => q.append(k, item));
      else q.set(k, String(v));
    });
    const qs = q.toString();
    return api.get(`/tenants/${tenantId}/inbox${qs ? `?${qs}` : ''}`);
  },
  patchTriage: (tenantId, capsuleId, convId, data) =>
    api.patch(`/tenants/${tenantId}/capsules/${capsuleId}/conversations/${convId}/triage`, data),
};

export const ticketsAPI = {
  // Si hay un agente trabajando en el caso **ahora**, y que paso con el que no termino.
  // Junta las cuatro puertas del caso —mail, WhatsApp, consola y el Asistente IA de la propia
  // ficha, que corre sobre una conversacion aparte— porque preguntar por una sola dejaba
  // afuera justo los turnos que se disparan desde la pantalla que la persona esta mirando.
  actividadAgentica: (tenantId, capsuleId, ticketId) =>
    api.get(`/tenants/${tenantId}/capsules/${capsuleId}/tickets/${ticketId}/actividad-agentica`),
  list: (tenantId, capsuleId, params) => {
    const query = params ? `?${new URLSearchParams(params)}` : '';
    return api.get(`/tenants/${tenantId}/capsules/${capsuleId}/tickets${query}`);
  },
  get: (tenantId, capsuleId, ticketId) => api.get(`/tenants/${tenantId}/capsules/${capsuleId}/tickets/${ticketId}`),
  // Autorizar no manda parametros a proposito: se ejecutan los que quedaron guardados cuando
  // el agente lo pidio. Si el cliente pudiera mandar otros, se aprobaria una cosa y se
  // ejecutaria otra, y quedaria registrado que esta persona autorizo la segunda.
  // Pedir una autorizacion sobre un caso. **Se pueden pedir varias**: dos gerentes, el
  // director y el jefe del area son cuatro autorizaciones legitimas sobre el mismo caso.
  crearAutorizacion: (tenantId, capsuleId, ticketId, body) =>
    api.post(`/tenants/${tenantId}/capsules/${capsuleId}/tickets/${ticketId}/autorizaciones`, body),
  aprobarHerramienta: (tenantId, capsuleId, ticketId, taskId) =>
    api.post(`/tenants/${tenantId}/capsules/${capsuleId}/tickets/${ticketId}/aprobaciones/${taskId}/aprobar`),
  rechazarHerramienta: (tenantId, capsuleId, ticketId, taskId, motivo) =>
    api.post(`/tenants/${tenantId}/capsules/${capsuleId}/tickets/${ticketId}/aprobaciones/${taskId}/rechazar`, { motivo }),
  // Cambiar el solicitante es cambiar quien ve el caso: endpoint propio, con motivo, y el
  // agente no puede llamarlo (`solicitante.cambiar` tiene `agente: False` en la matriz).
  setSolicitante: (tenantId, capsuleId, ticketId, contactId, motivo = '') =>
    api.put(`/tenants/${tenantId}/capsules/${capsuleId}/tickets/${ticketId}/solicitante`,
      { contact_id: contactId, motivo }),
  patch: (tenantId, capsuleId, ticketId, data) =>
    api.patch(`/tenants/${tenantId}/capsules/${capsuleId}/tickets/${ticketId}`, data),
  patchRoutingMetadata: (tenantId, capsuleId, ticketId, data) =>
    api.patch(`/tenants/${tenantId}/capsules/${capsuleId}/tickets/${ticketId}/routing-metadata`, data),
  addManagementNote: (tenantId, capsuleId, ticketId, text) =>
    api.post(`/tenants/${tenantId}/capsules/${capsuleId}/tickets/${ticketId}/management-note`, { text }),
  create: (tenantId, capsuleId, data) => api.post(`/tenants/${tenantId}/capsules/${capsuleId}/tickets`, data),
  // `resolucion` viaja sólo cuando la hay: el backend la exige si el estado destino la pide, y
  // mandar `undefined` es lo mismo que no mandarla.
  // Casos parecidos, **o el motivo de que no haya**. Va aparte del `get` porque consulta la
  // memoria y puede tocar el índice vectorial: no se le cobra a cada apertura de un caso.
  similares: (tenantId, capsuleId, ticketId) =>
    api.get(`/tenants/${tenantId}/capsules/${capsuleId}/tickets/${ticketId}/similares`),
  updateStatus: (tenantId, capsuleId, ticketId, status, resolucion) =>
    api.put(`/tenants/${tenantId}/capsules/${capsuleId}/tickets/${ticketId}/status`,
      resolucion ? { status, resolucion } : { status }),
  delete: (tenantId, capsuleId, ticketId) => api.delete(`/tenants/${tenantId}/capsules/${capsuleId}/tickets/${ticketId}`),
  purgeAll: (tenantId, body) => api.post(`/tenants/${tenantId}/tickets/purge-all`, body),
  orphanScrub: (tenantId, body) => api.post(`/tenants/${tenantId}/ops/orphan-scrub`, body),
  // Los cuatro botones de siempre —vaciar tickets, huerfanos, vaciar conversaciones, eliminar
  // consumos— mas la memoria de los casos, que no tenia boton. No borra nada nuevo: llama a las
  // mismas funciones. Solo admin.
  borradoOperativoTotal: (tenantId, body) =>
    api.post(`/tenants/${tenantId}/ops/borrado-operativo-total`, body),
  getWorkspace: (tenantId, capsuleId, ticketId) => api.get(`/tenants/${tenantId}/capsules/${capsuleId}/tickets/${ticketId}/workspace`),
  getWorkspaceTree: (tenantId, capsuleId, ticketId) => api.get(`/tenants/${tenantId}/capsules/${capsuleId}/tickets/${ticketId}/workspace/tree`),
  getArtifacts: (tenantId, capsuleId, ticketId) => api.get(`/tenants/${tenantId}/capsules/${capsuleId}/tickets/${ticketId}/workspace/artifacts`),
  createArtifact: (tenantId, capsuleId, ticketId, data) =>
    api.post(`/tenants/${tenantId}/capsules/${capsuleId}/tickets/${ticketId}/workspace/artifacts`, data),
  updateArtifact: (tenantId, capsuleId, ticketId, artifactId, data) =>
    api.put(`/tenants/${tenantId}/capsules/${capsuleId}/tickets/${ticketId}/workspace/artifacts/${artifactId}`, data),
  deleteArtifact: (tenantId, capsuleId, ticketId, artifactId) =>
    api.delete(`/tenants/${tenantId}/capsules/${capsuleId}/tickets/${ticketId}/workspace/artifacts/${artifactId}`),
  chat: (tenantId, capsuleId, ticketId, data, opts = {}) =>
    api.post(`/tenants/${tenantId}/capsules/${capsuleId}/tickets/${ticketId}/chat`, data, {
      timeout: opts.timeoutMs ?? httpTurnClientTimeoutMs(),
    }),
  getChatHistory: (tenantId, capsuleId, ticketId) => api.get(`/tenants/${tenantId}/capsules/${capsuleId}/tickets/${ticketId}/chat-history`),
  getConversation: (tenantId, capsuleId, ticketId, params) => {
    const query = params ? `?${new URLSearchParams(params)}` : '';
    return api.get(`/tenants/${tenantId}/capsules/${capsuleId}/tickets/${ticketId}/conversation${query}`);
  },
  getConversations: (tenantId, capsuleId, ticketId) =>
    api.get(`/tenants/${tenantId}/capsules/${capsuleId}/tickets/${ticketId}/conversations`),
  // `proponer` devuelve la propuesta **sin escribirla**: esto pisa titulo, objetivo y descripcion
  // escritos a mano, asi que la pantalla la carga en el formulario y la guarda una persona.
  refreshNarrative: (tenantId, capsuleId, ticketId, { proponer = false } = {}) =>
    api.post(`/tenants/${tenantId}/capsules/${capsuleId}/tickets/${ticketId}/refresh-narrative`,
      null, { params: proponer ? { proponer: true } : {} }),
  reindexWorkspace: (tenantId, capsuleId, ticketId) => api.post(`/tenants/${tenantId}/capsules/${capsuleId}/tickets/${ticketId}/reindex-workspace`),
  replyToUser: (tenantId, capsuleId, ticketId, data) => api.post(`/tenants/${tenantId}/capsules/${capsuleId}/tickets/${ticketId}/reply`, data),
  getChannelInfo: (tenantId, capsuleId, ticketId) => api.get(`/tenants/${tenantId}/capsules/${capsuleId}/tickets/${ticketId}/channel-info`),
  listTasks: (tenantId, capsuleId, ticketId, params) => {
    const q = params ? `?${new URLSearchParams(params)}` : '';
    return api.get(`/tenants/${tenantId}/capsules/${capsuleId}/tickets/${ticketId}/tasks${q}`);
  },
  createTask: (tenantId, capsuleId, ticketId, data) =>
    api.post(`/tenants/${tenantId}/capsules/${capsuleId}/tickets/${ticketId}/tasks`, data),
  getTask: (tenantId, capsuleId, ticketId, taskId) =>
    api.get(`/tenants/${tenantId}/capsules/${capsuleId}/tickets/${ticketId}/tasks/${taskId}`),
  deleteTask: (tenantId, capsuleId, ticketId, taskId) =>
    api.delete(`/tenants/${tenantId}/capsules/${capsuleId}/tickets/${ticketId}/tasks/${taskId}`),
  moverCompromiso: (tenantId, capsuleId, ticketId, data) =>
    api.put(`/tenants/${tenantId}/capsules/${capsuleId}/tickets/${ticketId}/compromiso`, data),
  patchTask: (tenantId, capsuleId, ticketId, taskId, data) =>
    api.patch(`/tenants/${tenantId}/capsules/${capsuleId}/tickets/${ticketId}/tasks/${taskId}`, data),
  patchCoordinatorState: (tenantId, capsuleId, ticketId, data) =>
    api.patch(`/tenants/${tenantId}/capsules/${capsuleId}/tickets/${ticketId}/coordinator-state`, data),
  getProject: (tenantId, capsuleId, ticketId) =>
    api.get(`/tenants/${tenantId}/capsules/${capsuleId}/tickets/${ticketId}/project`),
  applyWorkflow: (tenantId, capsuleId, ticketId, params) => {
    const q = params ? `?${new URLSearchParams(params)}` : '';
    return api.post(`/tenants/${tenantId}/capsules/${capsuleId}/tickets/${ticketId}/apply-workflow${q}`);
  },
  tomar: (tenantId, capsuleId, ticketId) =>
    api.post(`/tenants/${tenantId}/capsules/${capsuleId}/tickets/${ticketId}/tomar`),
  // Tomar una **tarea** de la cola de un rol. Es el mismo verbo una unidad mas abajo: la plantilla
  // reparte solo su fase de entrada, y las que siguen las toma quien pueda.
  tomarTarea: (tenantId, capsuleId, ticketId, taskId) =>
    api.post(`/tenants/${tenantId}/capsules/${capsuleId}/tickets/${ticketId}/tasks/${taskId}/tomar`),
  // «Esto no es mio»: vuelve a la cola de su rol, con el motivo escrito. El motivo no es opcional.
  devolverTarea: (tenantId, capsuleId, ticketId, taskId, data) =>
    api.post(`/tenants/${tenantId}/capsules/${capsuleId}/tickets/${ticketId}/tasks/${taskId}/devolver`, data),
  traspasar: (tenantId, capsuleId, ticketId, data) =>
    api.put(`/tenants/${tenantId}/capsules/${capsuleId}/tickets/${ticketId}/propietario`, data),
  patchPlanning: (tenantId, capsuleId, ticketId, data) =>
    api.patch(`/tenants/${tenantId}/capsules/${capsuleId}/tickets/${ticketId}/planning`, data),
  updatePhases: (tenantId, capsuleId, ticketId, phases) =>
    api.put(`/tenants/${tenantId}/capsules/${capsuleId}/tickets/${ticketId}/phases`, { phases }),
  listTimeEntries: (tenantId, capsuleId, ticketId, params) => {
    const q = params ? `?${new URLSearchParams(params)}` : '';
    return api.get(`/tenants/${tenantId}/capsules/${capsuleId}/tickets/${ticketId}/time-entries${q}`);
  },
  createTimeEntry: (tenantId, capsuleId, ticketId, data) =>
    api.post(`/tenants/${tenantId}/capsules/${capsuleId}/tickets/${ticketId}/time-entries`, data),
  updateTimeEntry: (tenantId, capsuleId, ticketId, entryId, data) =>
    api.patch(`/tenants/${tenantId}/capsules/${capsuleId}/tickets/${ticketId}/time-entries/${entryId}`, data),
  deleteTimeEntry: (tenantId, capsuleId, ticketId, entryId) =>
    api.delete(`/tenants/${tenantId}/capsules/${capsuleId}/tickets/${ticketId}/time-entries/${entryId}`),
  addExternalRef: (tenantId, capsuleId, ticketId, data) =>
    api.post(`/tenants/${tenantId}/capsules/${capsuleId}/tickets/${ticketId}/external-refs`, data),
  deleteExternalRef: (tenantId, capsuleId, ticketId, refType, refId) =>
    api.delete(`/tenants/${tenantId}/capsules/${capsuleId}/tickets/${ticketId}/external-refs/${refType}/${refId}`),
  refreshExternalRefs: (tenantId, capsuleId, ticketId) =>
    api.post(`/tenants/${tenantId}/capsules/${capsuleId}/tickets/${ticketId}/external-refs/refresh`),
  listCoordinatorRuns: (tenantId, capsuleId, ticketId, params) => {
    const q = params ? `?${new URLSearchParams(params)}` : '';
    return api.get(`/tenants/${tenantId}/capsules/${capsuleId}/tickets/${ticketId}/coordinator-runs${q}`);
  },
  listScheduleLinks: (tenantId, capsuleId, params) =>
    api.get(`/tenants/${tenantId}/capsules/${capsuleId}/tickets/schedule-links`, { params }),
  searchScheduleEntities: (tenantId, capsuleId, params) =>
    api.get(`/tenants/${tenantId}/capsules/${capsuleId}/tickets/schedule-links/search`, { params }),
  createScheduleLink: (tenantId, capsuleId, data) =>
    api.post(`/tenants/${tenantId}/capsules/${capsuleId}/tickets/schedule-links`, data),
  // Cambiarle el tipo o la espera a un vinculo que ya existe. **No se hace con `create`**: ese
  // deduplica y devuelve el vinculo viejo sin tocar el tipo del pedido.
  patchScheduleLink: (tenantId, capsuleId, linkId, data) =>
    api.patch(`/tenants/${tenantId}/capsules/${capsuleId}/tickets/schedule-links/${linkId}`, data),
  deleteScheduleLink: (tenantId, capsuleId, linkId) =>
    api.delete(`/tenants/${tenantId}/capsules/${capsuleId}/tickets/schedule-links/${linkId}`),
  getScheduleBlockers: (tenantId, capsuleId, params) =>
    api.get(`/tenants/${tenantId}/capsules/${capsuleId}/tickets/schedule-links/blockers`, { params }),
  // Los dos llamados de la replanificacion. `simular` **no escribe**: devuelve que se movería.
  simularReplanificacion: (tenantId, capsuleId, ticketId, data) =>
    api.post(`/tenants/${tenantId}/capsules/${capsuleId}/tickets/${ticketId}/replanificacion/simular`, data),
  aplicarReplanificacion: (tenantId, capsuleId, ticketId, data) =>
    api.post(`/tenants/${tenantId}/capsules/${capsuleId}/tickets/${ticketId}/replanificacion/aplicar`, data),
};

export const orgMemoryAPI = {
  flags: (tenantId, capsuleId) =>
    api.get(`/tenants/${tenantId}/capsules/${capsuleId}/org-memory/flags`),
  getProfile: (tenantId, capsuleId) =>
    api.get(`/tenants/${tenantId}/capsules/${capsuleId}/org-memory/knowledge-profile`),
  putProfile: (tenantId, capsuleId, knowledge_profile) =>
    api.put(`/tenants/${tenantId}/capsules/${capsuleId}/org-memory/knowledge-profile`, {
      knowledge_profile,
    }),
  bootstrapSistema: (tenantId, capsuleId) =>
    api.post(`/tenants/${tenantId}/capsules/${capsuleId}/org-memory/bootstrap/sistema`),
  getByTicket: (tenantId, capsuleId, ticketId) =>
    api.get(`/tenants/${tenantId}/capsules/${capsuleId}/org-memory/events/by-ticket/${ticketId}`),
  extract: (tenantId, capsuleId, ticketId) =>
    api.post(`/tenants/${tenantId}/capsules/${capsuleId}/org-memory/events/extract/${ticketId}`),
  patchEvent: (tenantId, capsuleId, eventId, data) =>
    api.patch(`/tenants/${tenantId}/capsules/${capsuleId}/org-memory/events/${eventId}`, data),
  approveEvent: (tenantId, capsuleId, eventId, data) =>
    api.post(`/tenants/${tenantId}/capsules/${capsuleId}/org-memory/events/${eventId}/approve`, data || {}),
  archiveEvent: (tenantId, capsuleId, eventId, data) =>
    api.post(`/tenants/${tenantId}/capsules/${capsuleId}/org-memory/events/${eventId}/archive`, data || {}),
  deleteEvent: (tenantId, capsuleId, eventId, params) =>
    api.delete(`/tenants/${tenantId}/capsules/${capsuleId}/org-memory/events/${eventId}`, { params }),
  listEvents: (tenantId, capsuleId, params) =>
    api.get(`/tenants/${tenantId}/capsules/${capsuleId}/org-memory/events`, { params }),
  search: (tenantId, capsuleId, data) =>
    api.post(`/tenants/${tenantId}/capsules/${capsuleId}/org-memory/search`, data),
  listChapters: (tenantId, capsuleId) =>
    api.get(`/tenants/${tenantId}/capsules/${capsuleId}/org-memory/chapters`),
  listIndices: (tenantId, capsuleId, params) =>
    api.get(`/tenants/${tenantId}/capsules/${capsuleId}/org-memory/indices`, { params }),
  runCurator: (tenantId, capsuleId) =>
    api.post(`/tenants/${tenantId}/capsules/${capsuleId}/org-memory/curator/run`),
  listProposals: (tenantId, capsuleId, params) =>
    api.get(`/tenants/${tenantId}/capsules/${capsuleId}/org-memory/curator/proposals`, { params }),
  approveProposal: (tenantId, capsuleId, proposalId) =>
    api.post(
      `/tenants/${tenantId}/capsules/${capsuleId}/org-memory/curator/proposals/${proposalId}/approve`
    ),
  parametricCatalog: (tenantId, capsuleId) =>
    api.get(`/tenants/${tenantId}/capsules/${capsuleId}/org-memory/parametric/catalog`),
  listSnapshots: (tenantId, capsuleId) =>
    api.get(`/tenants/${tenantId}/capsules/${capsuleId}/org-memory/parametric/snapshots`),
  previewSnapshot: (tenantId, capsuleId, domain) =>
    api.get(`/tenants/${tenantId}/capsules/${capsuleId}/org-memory/parametric/snapshots/${domain}/preview`),
  deleteSnapshot: (tenantId, capsuleId, domain) =>
    api.delete(`/tenants/${tenantId}/capsules/${capsuleId}/org-memory/parametric/snapshots/${domain}`),
  representations: (tenantId, capsuleId) =>
    api.get(`/tenants/${tenantId}/capsules/${capsuleId}/org-memory/representations`),
  createSnapshot: (tenantId, capsuleId, data) =>
    api.post(`/tenants/${tenantId}/capsules/${capsuleId}/org-memory/parametric/snapshots`, data),
  listPlaybooks: (tenantId, capsuleId) =>
    api.get(`/tenants/${tenantId}/capsules/${capsuleId}/org-memory/playbooks`),
  createPlaybook: (tenantId, capsuleId, data) =>
    api.post(`/tenants/${tenantId}/capsules/${capsuleId}/org-memory/playbooks`, data),
  deletePlaybook: (tenantId, capsuleId, slug) =>
    api.delete(`/tenants/${tenantId}/capsules/${capsuleId}/org-memory/playbooks/${slug}`),
};

export const chatAPI = {
  send: (data) => api.post('/chat/send', data),
  cancelAgentRun: (runId) => api.post(`/chat/agent-runs/${runId}/cancel`),
  listConversations: () => api.get('/chat/conversations'),
  getConversation: (convId) => api.get(`/chat/conversations/${convId}`),
  downloadAttachment: (conversationId, messageId, index) =>
    api.get(`/chat/conversations/${conversationId}/messages/${messageId}/attachments/${index}/download`, {
      responseType: 'blob',
    }),
};

export const ensayoAPI = {
  correr: (tenantId, capsuleId, agentId, mensaje) =>
    api.post(`/tenants/${tenantId}/capsules/${capsuleId}/agents/${agentId}/ensayar`, { mensaje }),
  purgar: (tenantId, capsuleId, agentId) =>
    api.delete(`/tenants/${tenantId}/capsules/${capsuleId}/agents/${agentId}/ensayos`),
};

export const capacidadesAPI = {
  list: (tenantId, capsuleId = '') =>
    api.get('/capacidades', { params: { tenant_id: tenantId, capsule_id: capsuleId } }),
  // Lo que el motor sabe hacer. Sale de la tabla del codigo, no de la base: que una pantalla
  // arme esta lista a mano seria otra copia del mismo dato.
  delProducto: () => api.get('/capacidades/del-producto'),
  // Con que valores corre cada capacidad **y de donde sale cada uno**. La segunda mitad es lo
  // que permite leer "la capsula dice 4" en vez de un 4 pelado.
  parametros: (tenantId, capsuleId = '', agentId = '') =>
    api.get('/capacidades/parametros', {
      params: { tenant_id: tenantId, capsule_id: capsuleId, agent_id: agentId },
    }),
  guardarParametros: (capacidadId, body) =>
    api.put(`/capacidades/${capacidadId}/parametros`, body),
  declarar: (body) => api.post('/capacidades', body),
  borrar: (capacidadId, tenantId, capsuleId = '') =>
    api.delete(`/capacidades/${capacidadId}`, { params: { tenant_id: tenantId, capsule_id: capsuleId } }),
  atar: (capacidadId, body) => api.put(`/capacidades/${capacidadId}/vinculo`, body),
  desatar: (capacidadId, tenantId, capsuleId) =>
    api.delete(`/capacidades/${capacidadId}/vinculo`, { params: { tenant_id: tenantId, capsule_id: capsuleId } }),
};

export const conexionesAPI = {
  plantillas: () => api.get('/conexiones/plantillas'),
  list: (tenantId, capsuleId = '') => api.get('/conexiones', { params: { tenant_id: tenantId, capsule_id: capsuleId } }),
  get: (conexionId, tenantId, capsuleId = '') => api.get(`/conexiones/${conexionId}`, { params: { tenant_id: tenantId, capsule_id: capsuleId } }),
  save: (conexionId, body) => api.put(`/conexiones/${conexionId}`, body),
  remove: (conexionId, tenantId, capsuleId = '') => api.delete(`/conexiones/${conexionId}`, { params: { tenant_id: tenantId, capsule_id: capsuleId } }),
  probar: (conexionId, body) => api.post(`/conexiones/${conexionId}/probar`, body),
  // Que deja de funcionar si esta conexion se va. Va aparte del `get` porque recorre cuatro
  // colecciones: es una pregunta que solo se hace al desconectar, no en cada lectura.
  usos: (conexionId, tenantId) =>
    api.get(`/conexiones/${conexionId}/usos`, { params: { tenant_id: tenantId } }),
};

export const skillsAPI = {
  list: (params = {}) => api.get('/skills', { params }),
  get: (skillId) => api.get(`/skills/${skillId}`),
  create: (data) => api.post('/skills', data),
  update: (skillId, data) => api.put(`/skills/${skillId}`, data),
  delete: (skillId) => api.delete(`/skills/${skillId}`),
  updateConfig: (skillId, configValues) => api.put(`/skills/${skillId}/config`, { config_values: configValues }),
  getAgentSkills: (tenantId, capsuleId, agentId) => api.get(`/skills/agent/${tenantId}/${capsuleId}/${agentId}`),
  updateAgentSkills: (tenantId, capsuleId, agentId, skills) => api.put(`/skills/agent/${tenantId}/${capsuleId}/${agentId}`, { skills }),
  updateAgentModel: (tenantId, capsuleId, agentId, modelConfig) => api.put(`/skills/agent/${tenantId}/${capsuleId}/${agentId}/model`, { model_config: modelConfig }),
  generateContent: (data) => api.post('/skills/generate-content', data),
  testConnection: (skillId, configValues) => api.post(`/skills/${skillId}/test-connection`, configValues ? { config_values: configValues } : {}),
  validarHerramientas: (body) => api.post('/skills/validar', body),
  importarCurl: (body) => api.post('/skills/importar-curl', body),
  // Pegar lo que sea —Postman, curl, documentación, notas— y que vuelva la conexión, la
  // herramienta y la prosa. Las credenciales vuelven **aparte y sólo para guardarlas**.
  importarLoQueTengas: (body) => api.post('/skills/importar-lo-que-tengas', body),
  probarHerramienta: (body) => api.post('/skills/probar-herramienta', body),
};

export const knowledgeAPI = {
  stats: () => api.get('/knowledge/stats'),
  search: (data) => api.post('/knowledge/search', data),
  playground: (data) => api.post('/knowledge/playground', data),
  browse: (collectionType) => api.get(`/knowledge/browse/${collectionType}`),
  getSourceConfig: () => api.get('/knowledge/source-config'),
  updateSourceConfig: (data) => api.put('/knowledge/source-config', data),
  scan: (data = {}) => api.post('/knowledge/scan', data),
  sourceTree: () => api.get('/knowledge/source-tree'),
  files: () => api.get('/knowledge/files'),
  graphBuild: () => api.post('/knowledge/graph/build'),
  graphStats: () => api.get('/knowledge/graph/stats'),
  graphDependencies: (name, depth = 3, direction = 'both') =>
    api.get(`/knowledge/graph/dependencies/${encodeURIComponent(name)}?depth=${depth}&direction=${direction}`),
  graphSearch: (q) => api.get(`/knowledge/graph/search?q=${encodeURIComponent(q)}`),
  graphValidate: () => api.get('/knowledge/graph/validate'),
  graphExplain: (name, depth = 3) =>
    api.get(`/knowledge/graph/explain/${encodeURIComponent(name)}?depth=${depth}`),
  graphVisualization: (params) => api.get('/knowledge/graph/visualization', { params: params || {} }),
  graphExtractDocs: () => api.post('/knowledge/graph/extract-docs'),
  getConfig: () => api.get('/knowledge/config'),
  updateConfig: (data) => api.put('/knowledge/config', data),
  getAgentConfig: (capsuleId, agentId) => api.get(`/knowledge/config/agents/${capsuleId}/${agentId}`),
  updateAgentConfig: (capsuleId, agentId, data) =>
    api.put(`/knowledge/config/agents/${capsuleId}/${agentId}`, data),
  startIndexJob: () => api.post('/knowledge/index-jobs'),
  getActiveIndexJob: () => api.get('/knowledge/index-jobs/active'),
  getIndexJob: (jobId) => api.get(`/knowledge/index-jobs/${jobId}`),
  cancelIndexJob: (jobId) => api.post(`/knowledge/index-jobs/${jobId}/cancel`),
  listRepos: () => api.get('/knowledge/repos'),
  createRepo: (data) => api.post('/knowledge/repos', data),
  updateRepo: (repoId, data) => api.put(`/knowledge/repos/${repoId}`, data),
  deleteRepo: (repoId, { purge_index = false } = {}) =>
    api.delete(`/knowledge/repos/${repoId}`, { params: purge_index ? { purge_index: true } : {} }),
  testRepoConnection: (data) => api.post('/knowledge/repos/test-connection', data),
  testSavedRepoConnection: (repoId) => api.post(`/knowledge/repos/${repoId}/test-connection`),
  downloadRepo: (repoId) => api.post(`/knowledge/repos/${repoId}/download`),
  indexRepo: (repoId, { mode = 'incremental' } = {}) =>
    api.post(`/knowledge/repos/${repoId}/index`, { mode }),
  cancelRepoOperation: (repoId) => api.post(`/knowledge/repos/${repoId}/cancel`),
  getRepoTree: (repoId) => api.get(`/knowledge/repos/${repoId}/tree`),
  setRepoIndexScope: (repoId, exclude_paths) =>
    api.put(`/knowledge/repos/${repoId}/index-scope`, { exclude_paths }),
  navigation: (qs = '') => api.get(`/knowledge/navigation${qs}`),
  getCapsuleRepoAccess: (capsuleId) => api.get(`/knowledge/capsules/${capsuleId}/repo-access`),
  updateCapsuleRepoAccess: (capsuleId, data) => api.put(`/knowledge/capsules/${capsuleId}/repo-access`, data),
  getAgentRepoScope: (capsuleId, agentId) => api.get(`/knowledge/agents/${capsuleId}/${agentId}/repo-scope`),
  updateAgentRepoScope: (capsuleId, agentId, data) => api.put(`/knowledge/agents/${capsuleId}/${agentId}/repo-scope`, data),
  getAgentSourceScope: (capsuleId, agentId) => api.get(`/knowledge/agents/${capsuleId}/${agentId}/source-scope`),
  updateAgentSourceScope: (capsuleId, agentId, data) => api.put(`/knowledge/agents/${capsuleId}/${agentId}/source-scope`, data),
  listManualFiles: (capsuleId) => api.get(`/knowledge/capsules/${capsuleId}/manual-files`),
  uploadManualFile: (capsuleId, formData) => api.post(`/knowledge/capsules/${capsuleId}/manual-files/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  listSourceMounts: () => api.get('/knowledge/source-mounts'),
  createSourceMount: (data) => api.post('/knowledge/source-mounts', data),
  updateSourceMount: (mountId, data) => api.put(`/knowledge/source-mounts/${mountId}`, data),
  deleteSourceMount: (mountId) => api.delete(`/knowledge/source-mounts/${mountId}`),
  reindexSourceMount: (mountId) => api.post(`/knowledge/source-mounts/${mountId}/reindex`),
  cancelSourceMountOperation: (mountId) => api.post(`/knowledge/source-mounts/${mountId}/cancel`),
  purgeReindexMounts: () => api.post('/knowledge/source-mounts/purge-reindex'),
  getSourceMountTree: (mountId) => api.get(`/knowledge/source-mounts/${mountId}/tree`),
  uploadSourceMountFiles: (mountId, formData) => api.post(
    `/knowledge/source-mounts/${mountId}/upload-files`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 600000 },
  ),
  uploadSourceMountZip: (mountId, formData) => api.post(
    `/knowledge/source-mounts/${mountId}/upload-zip`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 600000 },
  ),
  getCapsuleSourceAccess: (capsuleId) => api.get(`/knowledge/capsules/${capsuleId}/source-access`),
  updateCapsuleSourceAccess: (capsuleId, data) => api.put(`/knowledge/capsules/${capsuleId}/source-access`, data),
  getCapsuleKbAccess: (capsuleId) => api.get(`/knowledge/capsules/${capsuleId}/kb-access`),
  updateCapsuleKbAccess: (capsuleId, data) => api.put(`/knowledge/capsules/${capsuleId}/kb-access`, data),
  generateRepoWebhookSecret: (repoId) => api.post(`/knowledge/repos/${repoId}/webhook-secret`),
};

export const testAPI = {
  classify: (message) => api.post('/test/classify', { message }),
};

export const feedbackAPI = {
  rate: (data) => api.post('/feedback/rate', data),
  correct: (data) => api.post('/feedback/correct', data),
  learn: (data) => api.post('/feedback/learn', data),
  flag: (data) => api.post('/feedback/flag', data),
  stats: (capsuleId) => api.get(`/feedback/stats/${capsuleId}`),
};

export const logsAPI = {
  status: () => api.get('/logs/status'),
  platformTurnBudget: () => api.get('/logs/platform-turn-budget'),
  toggle: (enabled) => api.post('/logs/toggle', { enabled }),
  conversation: (conversationId, params) => api.get(`/logs/conversation/${conversationId}`, { params }),
  configReport: (conversationId, params) => api.get(`/logs/conversation/${conversationId}/config-report`, { params }),
  recent: (params) => api.get('/logs/recent', { params }),
  clear: (conversationId) => api.delete(`/logs/conversation/${conversationId}`),
};

/** Errores e incidencias persistidos en MongoDB (ver pestaña Logs en la app). */
export const diagnosticLogsAPI = {
  list: (params) => api.get('/diagnostic-logs', { params }),
  create: (data) => api.post('/diagnostic-logs', data),
  delete: (logId) => api.delete(`/diagnostic-logs/${logId}`),
  purge: (body) => api.post('/diagnostic-logs/purge', body || {}),
  tiposDeFalla: (params) => api.get('/diagnostic-logs/tipos-de-falla', { params }),
};

let _diagLogging = false;
api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const cfg = err.config || {};
    const url = String(cfg.url || '');
    if (_diagLogging) return Promise.reject(err);
    // `/conformidad` es la unica ruta publica: un link vencido o ya usado es un desenlace
    // normal, no un error del sistema. Y ademas no hay sesion para escribir el log.
    if (url.includes('/diagnostic-logs') || url.includes('/auth/login') || url.includes('/auth/segundo-factor')
        || url.includes('/conformidad/')) return Promise.reject(err);
    const st = err.response?.status;
    const networkFail = err.isAxiosError && !err.response && err.code;
    if (st && st < 400) return Promise.reject(err);
    if (!st && !networkFail) return Promise.reject(err);
    // 401 on auth/bootstrap is expected without session; logging would deadlock with refresh interceptor.
    if (st === 401) return Promise.reject(err);
    _diagLogging = true;
    try {
      const { capsule_id: capFromUrl } = tenantCapsuleFromApiUrl(url);
      await api.post('/diagnostic-logs', {
        origin: 'frontend',
        action: `${(cfg.method || 'GET').toUpperCase()} ${url}`,
        message: formatApiError(err).slice(0, 500),
        detail: err.response?.data ?? { code: err.code, network: true },
        http_status: st ?? null,
        level: 'error',
        request_path: url,
        capsule_id: capFromUrl || undefined,
      });
    } catch (_) { /* evitar bucle */ }
    finally {
      _diagLogging = false;
    }
    return Promise.reject(err);
  }
);

// **Publica: no necesita sesion.** El que pidio el caso puede no tener cuenta — es la razon por
// la que `personas` esta separada de `users`.
export const conformidadAPI = {
  ver: (token) => api.get(`/conformidad/${token}`),
  contestar: (token, data) => api.post(`/conformidad/${token}`, data),
};

// Preguntarle algo a un agente **sin abrir un caso**. Para todos los del equipo: el Playground
// que existía estaba restringido a admin y supervisor, o sea que los que preguntan no podían.
export const consultaAPI = {
  agentes: (tenantId, capsuleId) =>
    api.get(`/tenants/${tenantId}/capsules/${capsuleId}/consulta/agentes`),
  preguntar: (tenantId, capsuleId, body) =>
    api.post(`/tenants/${tenantId}/capsules/${capsuleId}/consulta`, body),
};

export const capsuleConfigAPI = {
  get: (tenantId, capsuleId) => api.get(`/capsule-config/${tenantId}/${capsuleId}`),
  update: (tenantId, capsuleId, data) => api.put(`/capsule-config/${tenantId}/${capsuleId}`, data),
  reset: (tenantId, capsuleId) => api.post(`/capsule-config/${tenantId}/${capsuleId}/reset`),
  defaults: (tenantId, capsuleId) => api.get(`/capsule-config/${tenantId}/${capsuleId}/defaults`),
  // La matriz de quien ve que pantalla, con el catalogo de pantallas y roles adentro: mandarlo
  // escrito en el frontend seria volver a la lista de accesos a mano que este cambio vino a sacar.
  pantallas: (tenantId, capsuleId) =>
    api.get(`/capsule-config/${tenantId}/${capsuleId}/pantallas`),
};

export const myWorkAPI = {
  listTickets: (tenantId) => api.get(`/tenants/${tenantId}/me/tickets`),
  listTasks: (tenantId, params) => api.get(`/tenants/${tenantId}/me/tasks`, { params }),
  // La cola de tareas de mis roles: lo que esta disponible y nadie tomo todavia. Solo trae las
  // de la fase en curso de cada caso, asi que lo que aparece se puede empezar hoy.
  listTareasSinDueno: (tenantId, params) =>
    api.get(`/tenants/${tenantId}/me/tareas-sin-dueno`, { params }),
  // La bandeja: todo lo que se le aviso a esta persona, **haya salido por un canal o no**. Es lo
  // que hace posible que alguien elija enterarse acá en vez de por correo.
  listAvisos: (tenantId, params) => api.get(`/tenants/${tenantId}/me/avisos`, { params }),
  marcarAvisoLeido: (tenantId, avisoId) =>
    api.post(`/tenants/${tenantId}/me/avisos/${avisoId}/leido`),
  // Todo lo pendiente, de una. `titulo` acota a un lado del mostrador: limpiar los avisos de
  // trabajo no puede apagar en silencio los de lo que uno pidio.
  marcarTodosLeidos: (tenantId, titulo) =>
    api.post(`/tenants/${tenantId}/me/avisos/leidos`, null, { params: titulo ? { titulo } : {} }),
};

export const channelsAPI = {
  list: (params) => api.get('/channels', { params }),
  create: (data) => api.post('/channels', data),
  get: (channelId) => api.get(`/channels/${channelId}`),
  update: (channelId, data) => api.put(`/channels/${channelId}`, data),
  delete: (channelId) => api.delete(`/channels/${channelId}`),
  getAgentChannels: (tenantId, capsuleId, agentId) => api.get(`/channels/agent/${tenantId}/${capsuleId}/${agentId}`),
  updateAgentChannels: (tenantId, capsuleId, agentId, data) => api.put(`/channels/agent/${tenantId}/${capsuleId}/${agentId}`, data),
  // Las casillas de la organizacion, para elegir la del canal de una lista en vez de tipearla.
  // Contesta 200 siempre: si no se pudo traer, `error` dice en que paso se cayo.
  listarBuzones: (channelType, channelId) => api.get('/channels/buzones', {
    params: { channel_type: channelType, ...(channelId ? { channel_id: channelId } : {}) },
  }),
  getGraphStatus: (channelId) => api.get(`/channels/${channelId}/graph/status`),
  // La cuenta de un canal de Teams, conectada una vez por quien la tiene (2026-10-19).
  estadoCuentaConectada: (channelId) => api.get(`/channels/${channelId}/cuenta-conectada`),
  iniciarCuentaConectada: (channelId) => api.post(`/channels/${channelId}/cuenta-conectada/inicio`),
  desconectarCuentaConectada: (channelId) => api.delete(`/channels/${channelId}/cuenta-conectada`),
  // Como entra lo nuevo: barrido cada N minutos o aviso de Microsoft. Pasar a aviso crea la
  // suscripcion en el momento; si Microsoft la rechaza, el canal sigue como estaba.
  cambiarIngreso: (channelId, ingreso) => api.put(`/channels/${channelId}/graph/ingreso`, ingreso),
  subscribeGraph: (channelId) => api.post(`/channels/${channelId}/graph/subscribe`),
  subscribeGraphTeamsChat: (channelId) => api.post(`/channels/${channelId}/graph/subscribe-chat`),
  unsubscribeGraph: (channelId) => api.post(`/channels/${channelId}/graph/unsubscribe`),
  syncGraphInbox: (channelId, lookbackHours) =>
    api.post(`/channels/${channelId}/graph/sync`, { lookback_hours: lookbackHours }),
  syncGraphTeamsChat: (channelId, lookbackHours) =>
    api.post(`/channels/${channelId}/graph/sync-chat`, { lookback_hours: lookbackHours }),
};

// Lo que el área sabe de una persona, por cápsula. Sólo admin: una memoria dice cómo conviene
// atender a alguien y esa persona no la ve. El backend exige el rol; esto es la otra mitad.
// Cuanto historial puede traer un agente, y de quien. No es un booleano: son tres.
export const casosPasadosAPI = {
  alcance: (tenantId, capsuleId, agentId, alcance) =>
    api.put(`/tenants/${tenantId}/capsules/${encodeURIComponent(capsuleId)}/agents/${encodeURIComponent(agentId)}/alcance-de-casos`, { alcance }),
};

export const memoriasDePersonaAPI = {
  deLaPersona: (tenantId, contactId) =>
    api.get(`/memorias-de-persona/${tenantId}/persona/${encodeURIComponent(contactId)}`),
  deLaCapsula: (tenantId, capsuleId) =>
    api.get(`/memorias-de-persona/${tenantId}/${encodeURIComponent(capsuleId)}`),
  guardar: (tenantId, capsuleId, contactId, data) =>
    api.put(`/memorias-de-persona/${tenantId}/${encodeURIComponent(capsuleId)}/${encodeURIComponent(contactId)}`, data),
  interruptor: (tenantId, capsuleId, contactId, activa) =>
    api.post(`/memorias-de-persona/${tenantId}/${encodeURIComponent(capsuleId)}/${encodeURIComponent(contactId)}/interruptor`, { activa }),
  borrar: (tenantId, capsuleId, contactId) =>
    api.delete(`/memorias-de-persona/${tenantId}/${encodeURIComponent(capsuleId)}/${encodeURIComponent(contactId)}`),
  borrarHecho: (tenantId, capsuleId, contactId, hechoId) =>
    api.delete(`/memorias-de-persona/${tenantId}/${encodeURIComponent(capsuleId)}/${encodeURIComponent(contactId)}/hecho/${encodeURIComponent(hechoId)}`),
};

export const contactsAPI = {
  list: (params) => api.get('/contacts', { params }),
  // Por id. El `list` busca por nombre, apellido e identidades y **nunca por id**: para
  // mostrar lo que una pantalla guardó hace falta esto, no una búsqueda con el id de texto.
  get: (contactId) => api.get(`/contacts/${encodeURIComponent(contactId)}`),
  create: (data) => api.post('/contacts', data),
  update: (contactId, data) => api.put(`/contacts/${contactId}`, data),
  delete: (contactId) => api.delete(`/contacts/${contactId}`),
  addIdentity: (contactId, data) => api.post(`/contacts/${contactId}/identities`, data),
  replaceIdentities: (contactId, identities) => api.put(`/contacts/${contactId}/identities`, identities),
  merge: (targetContactId, sourceContactId) =>
    api.post(`/contacts/${targetContactId}/merge`, { source_contact_id: sourceContactId }),
  syncOrgProfiles: () => api.post('/contacts/sync-org-profile'),
  syncOrgProfileOne: (contactId) => api.post(`/contacts/${encodeURIComponent(contactId)}/sync-org-profile`),
  ingestSources: () => api.get('/contacts/ingest/sources'),
  ingestPreview: (params) => api.get('/contacts/ingest/preview', { params }),
  ingestStart: (data) => api.post('/contacts/ingest', data),
  ingestRun: (runId) => api.get(`/contacts/ingest/${encodeURIComponent(runId)}`),
};

export const whatsappAPI = {
  connect: (channelId, config) => api.post(`/whatsapp/connect/${channelId}`, undefined, config),
  getQR: (channelId) => api.get(`/whatsapp/qr/${channelId}`),
  getStatus: (channelId) => api.get(`/whatsapp/status/${channelId}`),
  disconnect: (channelId) => api.post(`/whatsapp/disconnect/${channelId}`),
  send: (data) => api.post('/whatsapp/send', data),
  /** Admin/supervisor: backend -> bridge /health + /diagnostics + canales WA del tenant */
  integrationStatus: () => api.get('/whatsapp/integration-status'),
};

export const teamAPI = {
  // `solo_equipo` por defecto en el backend: Equipo muestra a quien trabaja en la capsula.
  // Con `false` viene el directorio completo, para poder elegir a alguien y darle un rol.
  list: (soloEquipo = true) => api.get('/team', { params: { solo_equipo: soloEquipo } }),
  // Saca el segundo factor de una cuenta local: en el próximo login lo vuelve a dar de alta. Es la
  // única recuperación si alguien pierde el teléfono (2026-10-13). Sólo admin.
  resetearSegundoFactor: (email) => api.delete(`/team/${encodeURIComponent(email)}/segundo-factor`),
  create: (data) => api.post('/team', data),
  update: (email, data) => api.put(`/team/${email}`, data),
  delete: (email) => api.delete(`/team/${email}`),
  purgeUser: (email) => api.delete(`/team/${encodeURIComponent(email)}/purge`),
  // Personas del directorio que todavia no estan en el equipo. Tener login **no** es
  // requisito: a alguien precargado se le puede asignar trabajo y el aviso le llega por mail.
  available: (q = '') => api.get('/team/available', { params: q ? { q } : {} }),
  createUser: (data) => api.post('/team/create-user', data),
  updateCredentials: (email, data) => api.put(`/team/${email}/credentials`, data),
  testChannel: (email, data) => api.post(`/team/${encodeURIComponent(email)}/channels/test`, data),
  pendingApprovals: () => api.get('/team/pending-approvals'),
  approveUser: (email) => api.post(`/team/${encodeURIComponent(email)}/approve`),
  denyUser: (email) => api.post(`/team/${encodeURIComponent(email)}/deny`),
  syncOrgProfiles: () => api.post('/team/sync-org-profile'),
  syncOrgProfileOne: (email) => api.post(`/team/${encodeURIComponent(email)}/sync-org-profile`),
  // El jefe se **elige** de la lista de personas. `null` vuelve al organigrama de M365.
  setJefe: (email, jefeContactId) =>
    api.put(`/team/${encodeURIComponent(email)}/jefe`, { jefe_contact_id: jefeContactId }),
  // La corrección al organigrama. Campos vacíos = se borra la corrección y vuelve el valor de
  // M365, que nunca se había ido: la corrección es una capa encima, no una escritura sobre él.
  setPerfilManual: (email, campos) =>
    api.put(`/team/${encodeURIComponent(email)}/perfil-manual`, campos),
  setTelefonoPrincipal: (email, identityId) =>
    api.put(`/team/${encodeURIComponent(email)}/telefono-principal`, null, {
      params: { identity_id: identityId },
    }),
};

// Lo que pedi, y lo que pidio mi gente. Sin capsula: el alcance es por objeto --los casos
// cuyo solicitante soy yo o alguien a mi cargo-- y no por capsula.
// La lista de prioridad de la empresa, que arma Dirección (2026-10-09). La lista **entera** de casos.
export const prioridadesAPI = {
  get: (tenantId, capsuleId) => api.get(`/tenants/${tenantId}/capsules/${capsuleId}/prioridades`),
  ordenar: (tenantId, capsuleId, ticketIds, motivo = '') =>
    api.put(`/tenants/${tenantId}/capsules/${capsuleId}/prioridades`, { ticket_ids: ticketIds, motivo }),
};

export const misSolicitudesAPI = {
  list: (tenantId, params = {}) => api.get(`/tenants/${tenantId}/me/solicitudes`, { params }),
  get: (tenantId, ticketId) => api.get(`/tenants/${tenantId}/me/solicitudes/${ticketId}`),
  // La lista **entera** ordenada, no un delta: es idempotente, y una actualizacion perdida
  // sobreescribe en vez de corromper.
  guardarOrden: (tenantId, capsuleId, ticketIds, motivo = '') =>
    api.put(`/tenants/${tenantId}/me/solicitudes/orden`, {
      capsule_id: capsuleId, ticket_ids: ticketIds, motivo,
    }),
  // Las autorizaciones se deciden **desde el portal** y no desde el router de casos: quien
  // autoriza suele no estar en la cápsula del caso, y allá le darían 403 sobre su propia tarea.
  verAutorizacion: (tenantId, taskId) =>
    api.get(`/tenants/${tenantId}/me/autorizaciones/${encodeURIComponent(taskId)}`),
  aprobarAutorizacion: (tenantId, taskId, motivo = '') =>
    api.post(`/tenants/${tenantId}/me/autorizaciones/${encodeURIComponent(taskId)}/aprobar`, { motivo }),
  rechazarAutorizacion: (tenantId, taskId, motivo = '') =>
    api.post(`/tenants/${tenantId}/me/autorizaciones/${encodeURIComponent(taskId)}/rechazar`, { motivo }),
};

export const analyticsAPI = {
  summary: () => api.get('/analytics/summary'),
  ticketsByPriority: () => api.get('/analytics/tickets/by-priority'),
  ticketsByStatus: () => api.get('/analytics/tickets/by-status'),
  ticketsByCategory: () => api.get('/analytics/tickets/by-category'),
  ticketsTimeline: () => api.get('/analytics/tickets/timeline'),
  agentsPerformance: () => api.get('/analytics/agents/performance'),
  conversationsByChannel: () => api.get('/analytics/conversations/by-channel'),
  recentActivity: () => api.get('/analytics/recent-activity'),
  tokensSummary: () => api.get('/analytics/tokens/summary'),
  tokensByModel: () => api.get('/analytics/tokens/by-model'),
  tokensByPurpose: () => api.get('/analytics/tokens/by-purpose'),
  tokensByAgent: () => api.get('/analytics/tokens/by-agent'),
  tokensByConversation: () => api.get('/analytics/tokens/by-conversation'),
  tokensTimeline: () => api.get('/analytics/tokens/timeline'),
  tokensPricing: () => api.get('/analytics/tokens/pricing'),
  tokensPricingPut: (prices) => api.put('/analytics/tokens/pricing', { prices }),
  tokensPurge: (confirm) => api.post('/analytics/tokens/purge', { confirm }),
  // PMO / ejecutivo (Entrega H)
  pmoSummary: (params) => api.get('/analytics/pmo/summary', { params }),
  pmoByDepartment: (params) => api.get('/analytics/pmo/by-department', { params }),
  pmoByStatus: (params) => api.get('/analytics/pmo/by-status', { params }),
  pmoByPriority: (params) => api.get('/analytics/pmo/by-priority', { params }),
  pmoDesvioEnCurso: (params) => api.get('/analytics/pmo/desvio-en-curso', { params }),
  pmoWorkload: (params) => api.get('/analytics/pmo/workload', { params }),
  pmoAging: (params) => api.get('/analytics/pmo/aging', { params }),
  pmoPorCategoria: (params) => api.get('/analytics/pmo/por-categoria', { params }),
  pmoCubo: (params) => api.get('/analytics/pmo/cubo', { params }),
  pmoDimensiones: () => api.get('/analytics/pmo/dimensiones'),
  pmoDesvio: (params) => api.get('/analytics/pmo/desvio', { params }),
  pmoGantt: (params) => api.get('/analytics/pmo/gantt', { params }),
  pmoPlanVsCompromiso: (params) => api.get('/analytics/pmo/plan-vs-compromiso', { params }),
  pmoCapacidad: (params) => api.get('/analytics/pmo/capacidad', { params }),
  executiveSavedViews: () => api.get('/analytics/executive/saved-views'),
  createExecutiveSavedView: (data) => api.post('/analytics/executive/saved-views', data),
  deleteExecutiveSavedView: (viewId) => api.delete(`/analytics/executive/saved-views/${viewId}`),
};

export const heartbeatAPI = {
  getConfig: (capsuleId) => api.get('/heartbeat/config', { params: { capsule_id: capsuleId } }),
  updateConfig: (capsuleId, data) => api.put('/heartbeat/config', data, { params: { capsule_id: capsuleId } }),
  getConversation: (convId) => api.get(`/heartbeat/conversation/${convId}`),
  updateConversation: (convId, data) => api.put(`/heartbeat/conversation/${convId}`, data),
  notifications: () => api.get('/heartbeat/notifications'),
  status: () => api.get('/heartbeat/status'),
};

export const apiTokensAPI = {
  list: (tenantId, capsuleId) =>
    api.get(`/tenants/${tenantId}/capsules/${capsuleId}/api-tokens`),
  create: (tenantId, capsuleId, data) =>
    api.post(`/tenants/${tenantId}/capsules/${capsuleId}/api-tokens`, data),
  revoke: (tenantId, capsuleId, tokenId) =>
    api.delete(`/tenants/${tenantId}/capsules/${capsuleId}/api-tokens/${tokenId}`),
};

export default api;
